import signalsData from '../data/signals.json';

/**
 * Live signals — proof the flow is real. Reads from src/data/signals.json so the
 * entries can be updated without touching this component.
 *
 * Rules that must not be broken (see the brief):
 *  - Ships EMPTY. When there are no items the whole section renders nothing, and
 *    it appears automatically once the first real entry is added.
 *  - Never a company name. Each item names the SECTOR only.
 *  - No placeholder or example items are ever committed here.
 */
type Signal = {
  headline: string;
  sector: string;
  date: string;
  source: { label: string; url: string };
};

type SignalsFile = {
  lastUpdated: string;
  items: Signal[];
};

const data = signalsData as SignalsFile;

export default function LiveSignals() {
  const items = data.items ?? [];
  if (items.length === 0) return null;

  const updated =
    data.lastUpdated ||
    items.map((item) => item.date).sort().at(-1) ||
    '';

  return (
    <section className="bg-black pt-8">
      <div className="container-x">
        <div className="intro-block grid gap-8 border-t border-white/10 pt-16 md:grid-cols-[180px_1fr] md:gap-16">
          <div>
            <p className="font-mono text-xs uppercase tracking-label text-gold">
              Live signals
            </p>
            {updated && (
              <p className="mt-2 font-mono text-xs tracking-label text-stone">
                Updated {updated}
              </p>
            )}
          </div>
          <div>
            <h2 className="font-display text-section font-light text-white text-balance">
              What moved this week.
            </h2>
            <p className="mt-6 max-w-2xl font-sans text-body text-white/85">
              A sample of the live triggers we are working from. Sector only, with
              the public source, so you can see the flow is real.
            </p>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
              {items.map((item, index) => (
                <div
                  key={`${item.headline}-${index}`}
                  className="grain overflow-hidden rounded-4xl border border-white/10 bg-charcoal p-8"
                >
                  <div className="flex items-center justify-between gap-4">
                    <p className="font-mono text-[0.65rem] uppercase tracking-label text-gold">
                      {item.sector}
                    </p>
                    <p className="font-mono text-[0.65rem] tracking-label text-stone">
                      {item.date}
                    </p>
                  </div>
                  <p className="mt-4 font-sans text-body leading-relaxed text-white/85">
                    {item.headline}
                  </p>
                  {item.source?.url && (
                    <a
                      href={item.source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1 font-sans text-sm text-white/70 underline decoration-white/20 underline-offset-4 transition-colors hover:text-gold"
                    >
                      {item.source.label || 'Source'} →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
